// The one loader every Director task container reads through: a first load that shows `loading`,
// silent background refreshes (the shared ERP refresh tick) that never blank what is shown, and
// latest-wins ordering — a response requested before a newer request OR a local `mutate` is
// discarded, so a slow poll can never resurrect a row the user just changed.
import {
  type DependencyList,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ErpApiError } from '../../../../../modules/erp';
import { useErpRefresh } from '../../../../../modules/erp/use-erp-refresh';

export type LoadState = 'loading' | 'ready' | 'failed' | 'forbidden';

export interface Loaded<T> {
  state: LoadState;
  data: T | null;
  /** The last failure's message. While `ready`, a failed background refresh: show it quietly. */
  error: string | null;
  refreshing: boolean;
  /** Retry after a failure (shows `loading`), or revalidate silently when data is shown. */
  reload(): void;
  /** Replace the shown data after a local write; any response requested before it is dropped. */
  mutate(next: T | ((data: T) => T)): void;
}

type Snapshot<T> = Omit<Loaded<T>, 'reload' | 'mutate'>;

const LOADING: Snapshot<never> = {
  state: 'loading',
  data: null,
  error: null,
  refreshing: false,
};

export function errorStatus(error: unknown): number | undefined {
  return error instanceof ErpApiError ? error.status : undefined;
}

/** The server's own message when it sent one, else the error text. */
export function errorMessage(error: unknown): string {
  if (error instanceof ErpApiError) return error.detail || error.message;
  return error instanceof Error ? error.message : String(error);
}

/** 401/403/404: the caller may not see this (a hidden row is a 404, never an existence oracle). */
function isAccessLoss(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 401 || status === 403 || status === 404;
}

function fingerprint(data: unknown): string | null {
  try {
    return JSON.stringify(data);
  } catch {
    return null;
  }
}

export function useLoader<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList
): Loaded<T> {
  const [snap, setSnap] = useState<Snapshot<T>>(LOADING);
  const latestLoad = useRef(load);
  latestLoad.current = load;
  const shown = useRef(snap);
  shown.current = snap;
  const seq = useRef(0);
  const inflight = useRef<AbortController | null>(null);
  // The serialized form of the shown server data: an unchanged poll keeps the same reference, so
  // nothing below re-renders for it.
  const shownJson = useRef<string | null>(null);

  const cancel = useCallback(() => {
    seq.current++;
    inflight.current?.abort();
    inflight.current = null;
  }, []);

  const run = useCallback(
    (background: boolean, outer?: AbortSignal) => {
      cancel();
      const id = seq.current;
      const controller = new AbortController();
      inflight.current = controller;
      const abort = () => controller.abort();
      outer?.addEventListener('abort', abort);
      if (background) {
        setSnap(s => (s.refreshing ? s : { ...s, refreshing: true }));
      } else {
        shownJson.current = null;
        setSnap(s => (s.state === 'loading' && s.data === null ? s : LOADING));
      }
      return latestLoad
        .current(controller.signal)
        .then(
          data => {
            if (id !== seq.current) return;
            const json = fingerprint(data);
            const same = json !== null && json === shownJson.current;
            shownJson.current = json;
            setSnap(s => ({
              state: 'ready',
              data: same && s.data !== null ? s.data : data,
              error: null,
              refreshing: false,
            }));
          },
          (error: unknown) => {
            if (id !== seq.current) return;
            const message = errorMessage(error);
            if (isAccessLoss(error)) {
              // Access is gone: hide what was shown rather than keep serving it (fail closed).
              shownJson.current = null;
              setSnap({
                state: 'forbidden',
                data: null,
                error: message,
                refreshing: false,
              });
            } else if (background && shown.current.data !== null) {
              setSnap(s => ({ ...s, error: message, refreshing: false }));
            } else {
              setSnap({
                state: 'failed',
                data: null,
                error: message,
                refreshing: false,
              });
            }
          }
        )
        .finally(() => {
          outer?.removeEventListener('abort', abort);
          if (inflight.current === controller) inflight.current = null;
        });
    },
    [cancel]
  );

  // Layout effect: a deps change swaps to `loading` before the browser paints the old data.
  useLayoutEffect(() => {
    run(false).catch(() => {}); // run() resolves its own errors into state
    return cancel;
    // The caller's `deps` are the dependency list, exactly as with useEffect.
     
  }, deps);

  useErpRefresh(signal => {
    // The first load is still in flight; a poll must not supersede it.
    if (shown.current.state === 'loading') return;
    return run(true, signal);
  });

  const reload = useCallback(() => {
    run(shown.current.data !== null).catch(() => {}); // errors land in state
  }, [run]);

  const mutate = useCallback(
    (next: T | ((data: T) => T)) => {
      cancel();
      shownJson.current = null;
      setSnap(s => {
        if (typeof next !== 'function')
          return { ...s, state: 'ready', data: next, refreshing: false };
        if (s.data === null) return s;
        return {
          ...s,
          state: 'ready',
          data: (next as (data: T) => T)(s.data),
          refreshing: false,
        };
      });
    },
    [cancel]
  );

  return { ...snap, reload, mutate };
}
